/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Alert,
    Box,
    Button,
    Divider,
    Image,
    Heading,
    Stack,
    Link,
    Text,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import LoginFields from '../forms/login-fields'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getUrl} from '../../utils/cloudinary-utils'
import {KS_SIGNUP_URL} from '../../constants'
import useTagManager from '../../hooks/use-tag-manager'

const LoginForm = ({submitForm, clickForgotPassword = noop, form, showSignedInBtn = false}) => {
    const styles = useStyleConfig('LoginStyles')
    const {
        app: {commerceAPI}
    } = getConfig()
    const tagManager = useTagManager()
    const {hostUrl} = commerceAPI.storefront
    return (
        <Fragment>
            <Stack justify="center" align="center" spacing="10px">
                <Heading {...styles.modal.heading}>
                    <FormattedMessage defaultMessage="Sign in" id="sign.in" />
                </Heading>
                <Divider {...styles.modal.divider} />
                <Box {...styles.modal.descriptionBox}>
                    <Image
                        src={getUrl('UIImages/images/heart-circle-black.svg')}
                        height="20px"
                        width="20px"
                        alt="heart icon"
                        marginTop="3px"
                    />
                    <Text {...styles.modal.descriptionText}>
                        <FormattedMessage
                            id="sign.in.view.wishlist"
                            defaultMessage="Sign in to view or save to your wishlist."
                        />
                    </Text>
                </Box>
            </Stack>
            <form
                id="login-form"
                name="login-form"
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form"
            >
                <Stack spacing="20px">
                    {form.formState.errors?.global && (
                        <Alert status="error" {...styles.modal.alertBox}>
                            <Text {...styles.modal.alertText}>
                                {form.formState.errors.global.message}
                            </Text>
                        </Alert>
                    )}
                    <Stack spacing="0">
                        <LoginFields form={form} />

                        <Box textAlign="right" marginTop="20px">
                            <Button
                                variant="link"
                                {...styles.modal.link}
                                onClick={clickForgotPassword}
                            >
                                <FormattedMessage
                                    defaultMessage="Forgot password?"
                                    id="login_form.link.forgot_password"
                                />
                            </Button>
                        </Box>
                    </Stack>
                    <Stack spacing="10px">
                        <Button
                            type="submit"
                            onClick={() => {
                                form.clearErrors('global')
                                tagManager.loginInitiated()
                            }}
                            {...styles.modal.button}
                            isLoading={form.formState.isSubmitting}
                            gap="10px"
                        >
                            {showSignedInBtn && (
                                <Image
                                    src={getUrl('UIImages/images/tick-circle-white.svg')}
                                    height="14px"
                                    width="14px"
                                    alt="Signed In"
                                />
                            )}
                            {showSignedInBtn ? (
                                <FormattedMessage
                                    defaultMessage="Signed in"
                                    id="login_form.button.signed_in"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Sign in"
                                    id="login_form.button.sign_in"
                                />
                            )}
                        </Button>

                        <Stack direction="row" spacing={1} justify="center">
                            <Link
                                variant="link"
                                onClick={() => {
                                    tagManager.formFieldChange('login-form', 'Create Account')
                                    tagManager.ctaClick(
                                        'cta_click',
                                        'authentication',
                                        'create_account',
                                        'cart_authentication',
                                        'Create an Account'
                                    )
                                }}
                                {...styles.modal.link}
                                fontSize="17px"
                                href={hostUrl + KS_SIGNUP_URL}
                            >
                                <FormattedMessage
                                    defaultMessage="Create an Account"
                                    id="create.an.account"
                                />
                            </Link>
                        </Stack>
                    </Stack>
                </Stack>
            </form>
        </Fragment>
    )
}

LoginForm.propTypes = {
    submitForm: PropTypes.func,
    clickForgotPassword: PropTypes.func,
    showSignedInBtn: PropTypes.bool,
    clickCreateAccount: PropTypes.func,
    form: PropTypes.object
}

export default LoginForm
